import type { Lang } from "../../../shared/i18n";
import type {
  SchoolStatus,
  ContentState,
  Region,
} from "../../../shared/types/prisma-enums";
import type { RiskVerdict, SignalCode } from "../../../shared/api/platform";
import {
  OPERATOR_PASSWORD_MAX,
  OPERATOR_PASSWORD_MIN,
  type PasswordProblem,
} from "./platformModel";

/**
 * F1, F2, F4, F5 and X1/X6's copy.
 *
 * ⚠️ THIS TABLE HAD A MALAYALAM COLUMN (ADR-044), and `copy.test.ts` beside it
 * carried the untranslated-string gate that went with it.
 *
 * ⚠️ `Lang` IS IMPORTED FROM `shared/i18n`, NOT REDECLARED. The school tier has
 * its own local `Lang` in `school/copy.ts` for slice reasons; a third definition
 * of "the two languages this product speaks" would be one too many.
 *
 * 🔴 NO NUMBER IN THIS FILE IS A PERCENTAGE WITHOUT ITS DENOMINATOR. OD-39/OD-72
 * and `health-score.ts`'s own rule — *"a school where two of three children
 * played is not 67% engaged; it is two of three"*. The one place a rate is shown
 * at all is F1's headline, and it is shown **beside** its pair, never instead.
 */

export const PLATFORM_COPY = {
  en: {
    /* ── F1 ── */
    overviewTitle: "Dashboard",
    /**
     * ✅ THE SECTION ABOVE THE NUMBERS USED TO BE HEADED "Platform" TOO.
     *
     * `PageHead` already prints `overviewTitle` at the top of this screen, so
     * the same word appeared twice — once as the page's `<h1>` and again as the
     * heading of its first section, which tells a reader nothing they did not
     * learn four lines earlier. A section heading has one job: say what THIS
     * group of things is, as "Needs a phone call" does above it.
     */
    overviewTotals: "At a glance",
    overviewSubtitle:
      "Every school on Kalqy, and which of them needs a phone call.",
    schoolsTotal: "Schools",
    schoolsActive: "Active",
    schoolsOnboarding: "Onboarding",
    schoolsSuspended: "Suspended",
    schoolsArchived: "Archived",
    /** 🔴 Enrolment, never "seats sold". F3 is not built; this is a head count. */
    enrolment: "Children enrolled",
    enrolmentRule:
      "Children in a class that is still running, across active schools.",
    teachersActive: "Teachers",
    activeChildren: "Children who played",
    engagementWindow: (days: number) => `In the last ${days} days`,
    /** The pair, and only then the rate — the rate is never alone on the page. */
    engagementPair: (count: number, total: number, days: number) =>
      `${count} of ${total} enrolled children have played in the last ${days} days`,
    /**
     * ⚠️ THE ONE NUMBER ON THIS SCREEN A SCHOOL WOULD NEVER SEE. It is the size
     * of a data problem, not a metric: a child with no class is invisible to
     * every scoped query in the product, including their own school admin's.
     */
    unattributed: (n: number) =>
      `${n} children are in no class at all, so no school can see them. Every one of them is a roster that went wrong.`,
    unattributedNone: "Every child belongs to a class.",
    atRiskTitle: "Needs a phone call",
    atRiskNone: "No school is showing churn signals today.",
    /** 🔴 Never a bare score. The verdict is a word and the signal is a pair. */
    signalNoSeats: "No children enrolled",
    signalNoTeachers: "No teacher can sign in",
    signalNobodyPlayed: "Nobody has played",
    signalLowEngagement: "Few children are playing",
    verdictOk: "Fine",
    verdictWatch: "Worth a look",
    verdictAtRisk: "Needs a call",
    riskTooSmall: "Too few children for a share to mean anything",
    riskNotEnoughHistory: "Activated too recently to judge",
    riskNotActive: "Not an active school",
    riskOk: "Nothing showing",
    riskAtRisk: "Showing churn signals",
    allSchools: "Every school",
    openSchool: "Open",

    /* ── F2 · the lifecycle, as something you filter by ── */
    /**
     * 🔴 THE STATE WAS A COLUMN YOU SCANNED FORTY ROWS OF, AND THE QUESTION AN
     * OPERATOR ARRIVES WITH IS *"which schools are still onboarding"*. These are
     * the tiles that answer it, and every one of them is also the filter.
     */
    schoolFilterAll: "Every school",
    schoolFilterOnboarding: "Onboarding",
    schoolFilterActive: "Active",
    schoolFilterSuspended: "Suspended",
    schoolFilterArchived: "Archived",
    /**
     * ⚠️ ONE CLAUSE PER STATE, UNDER THE TILE. "Suspended" is a word an operator
     * can read two ways — the wrong one is "we deleted their data" — and
     * `admin-panel.md` §3 is explicit that it is a billing state.
     */
    schoolStateOnboarding: "Signed, not live yet.",
    schoolStateActive: "Live. Children are playing.",
    schoolStateSuspended: "Nobody can sign in. Nothing is deleted.",
    schoolStateArchived: "Read-only. Cannot be undone.",
    /** 🔴 The one figure on this screen that is a task rather than a fact. */
    schoolsNoAdmin: (n: number) =>
      n === 1
        ? "1 school has nobody who can sign in."
        : `${String(n)} schools have nobody who can sign in.`,
    schoolsNoAdminAction:
      "Open the school and create its first admin. Until then the account exists and nobody at it can get in.",
    schoolsFilteredNone: "No school is in that state.",
    schoolsShowing: (shown: number, total: number) =>
      `Showing ${String(shown)} of ${String(total)} schools`,

    /* ── F2 ── */
    schoolsTitle: "Schools",
    schoolsSubtitle:
      "Onboard a school, move it through its lifecycle, and give it its first admin. A school cannot create its own first admin.",
    schoolName: "School",
    status: "Status",
    /**
     * ⚠️ "State", NOT "Region", AND THE COLUMN IS THE SAME COLUMN. `Region` is
     * the schema's name for it and stays there; what an operator reads is the
     * thing they can actually answer — Kerala, Karnataka — rather than a word
     * that used to mean a continent.
     */
    region: "State",
    classes: "Classes",
    admins: "Admins",
    since: "Since",
    noAdminYet: "No admin yet",
    /** Zero admins is the state F2 exists to fix, so it is a sentence, not a 0. */
    noAdminConsequence:
      "Nobody at this school can sign in until it has an admin.",
    addSchool: "Onboard a school",
    addSchoolName: "School name",
    addSchoolRegion: "State",
    /**
     * 🔴 X2 — THE STATE IS THE KEY RETENTION HANGS OFF, and it is the one fact
     * about a school that cannot be worked out later: a child has no personal
     * data from which a jurisdiction could be derived (ADR-004).
     *
     * ⚠️ THE HINT SAYS WHAT IS TRUE TODAY, NOT WHAT MIGHT BE. Every state
     * carries the same 365-day window because DPDP is national
     * (`retention-policy.ts`), and a hint implying the choice changes a
     * deletion date would be a claim the code does not make. What it does
     * decide is which school appears where — the rollout planner and every
     * report filter read this column.
     */
    regionHint:
      "Where the school is. Retention is 365 days across India, so this does not change a deletion date — it is how schools are grouped and targeted.",
    create: "Onboard",
    creating: "Onboarding…",
    createFailed: "That school was not created.",
    nameTaken:
      "A school with that name already exists. Rename it or open the existing one.",

    lifecycle: "Lifecycle",
    statusCreated: "Created",
    statusOnboarding: "Onboarding",
    statusActive: "Active",
    statusSuspended: "Suspended",
    statusArchived: "Archived",
    moveTo: (status: string) => `Move to ${status}`,
    /** 🔴 admin-panel.md §3: suspension is a billing state, never a data action. */
    suspendMeaning:
      "Suspended means nobody at the school can sign in. Nothing is deleted, and the retention clock keeps running.",
    archiveMeaning:
      "Archived is read-only and cannot be undone. The retention clock keeps running while a school is archived, so a school restored later may no longer hold the data it had.",
    archiveConfirm: "Archive this school? This cannot be undone.",
    confirm: "Yes, archive it",
    cancel: "Cancel",
    terminal:
      "This school is archived. There is nothing further to move it to.",
    transitionFailed: "That did not change.",

    changeRegion: "Change the state",
    /** 🔴 The sentence an operator who thinks this "fixes retention" must read. */
    regionKept: (n: number) =>
      `${n} consents already recorded keep the deletion date they were given. Changing the state only affects consents recorded from now on.`,
    regionChanged: "State changed.",

    provisionTitle: "Give this school its first admin",
    /* ⚠️ IT USED TO END "and shows you a password once" — the generated path,
       which is gone. What it says now is the thing that is still true and still
       gets forgotten: you are the delivery channel. */
    provisionHint:
      "A school cannot create its own first admin. You choose the password here and hand it over yourself.",
    adminName: "Their name",
    adminEmail: "Their email",
    provision: "Create the admin",
    provisioning: "Creating…",
    emailTaken: "An account with that email already exists.",
    provisionFailed: "That admin was not created.",
    /*
     * ⛔ `credentialTitle`, `credentialOnce` AND `credentialChannel` STOOD HERE
     * — the three sentences that stopped a GENERATED credential being lost or
     * leaked. There is no generated credential any more (`school.dto.ts` on the
     * server), so all three described a card the product no longer draws.
     */
    credentialExisting: (n: number) =>
      `This school already had ${n} working admins. If that is a surprise, you may have just created a duplicate.`,
    credentialDone: "I have handed it over",

    /* ── F2 · the password, which is now the only way in ── */
    passwordSection: "Their password",
    /**
     * 🔴 IT SAYS WHAT THE OPERATOR HAS TO DO NEXT, WHICH IS THE PART THEY GET
     * WRONG. This used to describe two paths — leave it blank and Kalqy
     * generates one, or type one — because both existed. One does now, so the
     * sentence stops explaining a choice and starts explaining a consequence:
     * the school signs in with this, and you are the channel.
     */
    passwordHint:
      "You choose it, and you hand it over — there is still no email or WhatsApp invite. Three unrelated words is easy to read out and hard to guess.",
    passwordField: "Password",
    passwordConfirm: "Type it again",
    /**
     * ⚠️ AN INSTRUCTION, NOT A CORRECTION, AND `passwordState` KEEPS AN
     * `'empty'` KIND SO IT CAN BE ONE. Telling somebody their password is too
     * short before they have typed a character is how a form teaches people to
     * ignore its warnings.
     */
    passwordRequiredNote: "Choose a password for this account.",
    passwordReadyNote: "This school will sign in with the password you typed.",
    /* One sentence per reason. "That password is not allowed" with no cause is
       how somebody ends up appending digits until something is accepted. */
    passwordTooShort: (n: number) =>
      `Use at least ${n} characters. Three unrelated words works well.`,
    passwordTooLong: (n: number) => `That is longer than ${n} characters.`,
    passwordEdgeWhitespace:
      "That starts or ends with a space. The sign-in box will not remove it, so nobody could type this back.",
    passwordOneClass:
      "Mix letters with something else — a digit, a space or a symbol.",
    passwordPredictable:
      "That is one of the passwords attackers try first, or a small variation on one. Pick something unrelated.",
    passwordEchoesIdentity:
      "That contains the school name or the login address. Anyone who knows which school this is would try it early.",
    passwordMismatch: "The two boxes do not match.",
    /*
     * 🔴 THE RULE, DERIVED FROM THE CONSTANT, BECAUSE THE HAND-WRITTEN VERSION
     * WAS WRONG. `PlatformOperatorsScreen` printed *"at least 8 characters with
     * numbers or symbols"* against an `OPERATOR_PASSWORD_MIN` of 12 — so an
     * operator followed the instruction on the screen, got a valid-looking
     * password refused by a rule they were never shown, and reported the form
     * as broken. Nothing here restates a number that lives in code.
     */
    operatorPasswordHint: (n: number) =>
      `At least ${String(n)} characters, and letters mixed with something else — a digit, a space or a symbol. Three unrelated words works well.`,
    /** `passwordReadyNote`'s sibling. That one names a school; an operator has none. */
    operatorPasswordReadyNote: "This operator will sign in with the password you typed.",
    /** The card shown after an operator-set password. A different fact, so different copy. */
    credentialChosenTitle: "The account is ready",
    credentialChosenBody:
      "Hand over the password you typed. Kalqy does not show it again and cannot read it back.",
    credentialChangeAdvice:
      "Ask them to change it under Settings the moment they sign in.",

    /*
     * ✅ THE "HOW THEY GET IN" COPY IS BACK — CHANGE 19 §1. It was removed with
     * the invite path, and the reason it went is the reason it returns: *"there
     * is one way in now, so the chooser would have been a control between one
     * option."* There are two again, and the second one is the default.
     */
    credentialMailedTitle: "They have been emailed a link",
    /* 🔴 THE EXPIRY IS IN THE FIRST SENTENCE. A link that quietly died is the
       commonest reason this flow produces a phone call. */
    credentialMailedBody: (expires: string) =>
      expires === ""
        ? "They choose their own password from a single-use link. Nobody else ever knows it."
        : `They choose their own password from a single-use link. It stops working on ${expires}, and nobody else ever knows it.`,
    credentialMailedAdvice:
      "Nothing for you to pass on. If the link expires before they use it, open the school and send another — the old one dies when a new one is issued.",
    /* 🔴 A WARNING, NOT AN ERROR. The school, the account and the invite all
       exist; only the delivery failed, and starting over would collide on both
       the school name and the address. */
    credentialMailFailedTitle: "The school is created — the email did not go",
    credentialMailFailedBody:
      "The account and its set-up link both exist. What failed is only the sending, so nothing needs to be created again.",

    /*
     * ⛔ THE WHOLE "HOW THEY GET IN" SECTION STOOD HERE — a chooser between an
     * invitation link and a password, plus the five strings the invitation card
     * needed. There is one way in now, so the chooser would have been a control
     * between one option and the card would never render.
     *
     * ⚠️ THE OPERATOR-INVITE COPY IN `PlatformSystemScreens` WENT WITH IT for the
     * same reason. `#/invite/<token>` and its claim screen are untouched: links
     * already issued still work.
     */

    /* ── F2 · onboarding a school and its login together ── */
    addSchoolAdminSection: "Its first sign-in (optional)",
    /**
     * ⚠️ WHY IT IS ON THIS FORM AT ALL. Two requests means a browser can close
     * between them, and the school is then left with nobody who can sign in.
     */
    addSchoolAdminHint:
      "Fill this in and the school gets a working login in the same step. Leave it empty and you can add one later — until then nobody at the school can sign in.",
    addSchoolAdminSkipped:
      "No login yet. Open the school afterwards to add one.",

    /* ── F2 · one school in full ── */
    schoolOverview: "Overview",
    schoolPeople: "People",
    schoolClasses: "Classes",
    schoolSettingsTitle: "School settings",
    schoolHistory: "Recent activity",
    schoolFeatures: "Feature flags",
    schoolRollovers: "Academic years",
    detailLoadFailed: "Could not load this school.",
    /* 🔴 Opening a school is an audited read. Said before it is done, not after. */
    detailAudited:
      "Opening a school records who looked, in the audit log. It shows staff sign-in addresses.",
    peopleRole: "Role",
    peopleEmail: "Signs in with",
    peopleLastSeen: "Last signed in",
    peopleNever: "Never signed in",
    peopleDeactivated: "Deactivated",
    peopleNoEmail: "No sign-in address",
    peopleNone: "Nobody at this school can sign in yet.",
    classesTeacher: "Teacher",
    classesNoTeacher: "No teacher assigned",
    classesCode: "Class code",
    classesNoCode: "No code, so no child here can sign in",
    classesChildren: "Children",
    classesArchived: "Archived",
    classesNone: "No classes yet. The school admin creates them.",
    classesYear: "Year",
    countStudents: "Children enrolled",
    countActive: "Children who played",
    countClasses: "Classes",
    countStaff: "Staff who can sign in",
    /** A pair, never a bare count — the window travels with the number. */
    activeWindow: (days: number) => `In the last ${days} days`,
    archivedClasses: (n: number) =>
      `${n} archived classes are listed below, kept rather than deleted.`,
    settingRecording: "Recording allowed school-wide",
    settingLanguage: "Default language",
    settingYear: "Academic year",
    settingUnset: "Not set",
    yes: "Yes",
    no: "No",
    featuresNone: "No feature flags set for this school.",
    rolloversNone: "No academic-year rollover has been run for this school.",
    rolloverRow: (from: string, to: string) => `${from} to ${to}`,
    rolloverIncomplete: "Started, not finished",
    /** 🔴 The list excludes this page's own reads, and says so rather than pretending to be whole. */
    historyNote:
      "The most recent changes to this school. Reads of this page are left out; the full log is under Audit.",
    historyNone: "Nothing has been changed on this school yet.",

    /* ── F4 ── */
    contentTitle: "Content library",
    contentSubtitle:
      "Which version of the games each school is playing, and how a new one gets there.",
    /**
     * 🔴 THE FOUR STATES, EXPLAINED WHERE THEY ARE USED RATHER THAN LEFT TO BE
     * INFERRED FROM THEIR NAMES.
     *
     * The screen showed four one-word pills — Draft, Beta, Published, Retired —
     * and two paragraphs of consequence at the bottom of the page, three
     * sections below the table those words are in. So an operator deciding
     * whether to press "Publish" read the button, not the sentence about what it
     * does to children mid-way through a game.
     *
     * ⚠️ EACH IS ONE CLAUSE ABOUT WHO SEES WHAT. That is the only question this
     * screen is ever asked, and the four answers are genuinely different.
     */
    stateExplainDraft: "Nobody sees it. Yours to work on.",
    stateExplainBeta: "Only schools you have opted in.",
    stateExplainPublished: "Every school.",
    stateExplainRetired: "Nobody new. What children already did still counts.",
    /**
     * ✅ THE ONE-LINE VERSION OF THE TWO PARAGRAPHS THAT SAT AT THE FOOT OF THE
     * PAGE. They are still there in full — see `releaseForward` and
     * `retireMeaning` — but the sentence that changes a decision now sits beside
     * the button that makes it.
     */
    releaseForwardShort: "Forward only. A published release cannot go back.",
    contentHowTo:
      "Cut a draft, opt a school or two in with beta, then publish. Each step is one button on the row.",
    version: "Version",
    state: "State",
    notes: "Notes",
    published: "Published",
    retired: "Retired",
    created: "Created",
    stateDraft: "Draft",
    stateBeta: "Beta",
    statePublished: "Published",
    stateRetired: "Retired",
    newRelease: "Cut a release",
    newReleaseVersion: "Version",
    newReleaseNotes: "What changed",
    releaseAlwaysDraft: "A new release always starts as a draft.",
    releaseForward:
      "A published release cannot go back to draft or beta. Children have played it and their activity carries its version.",
    /** ⚠️ Retiring hides it from new sessions; it never rewrites history. */
    retireMeaning:
      "Retiring hides a release from new sessions. Everything children have already done keeps pointing at it, so old reports still make sense.",
    versionTaken: "A release with that version already exists.",
    releaseFailed: "That release was not created.",
    contentEmpty:
      "No releases yet. Cut one when there is something to publish.",

    /* ── F5 ── */
    auditTitle: "Audit log",
    auditSubtitle:
      "Every privileged action, newest first. Append-only: nothing here can be edited or deleted.",
    /** 🔴 The read is itself logged, and an operator should know before paging. */
    auditIsAudited:
      "Opening this page writes a line to this log. That is deliberate — a read nobody can reconstruct later is indistinguishable from a breach.",
    when: "When",
    actor: "Who",
    action: "What",
    target: "On what",
    summary: "Detail",
    system: "System",
    filterAction: "Action",
    filterActor: "Actor id",
    filterTarget: "Target id",
    filterFrom: "From",
    filterTo: "To",
    filterAny: "Anything",
    /** ⚠️ The UTC boundary, stated rather than discovered mid-incident. */
    auditUtc:
      "Dates are UTC and a “to” date includes the whole of that day. In India that runs from 05:30 to 05:30.",
    apply: "Apply",
    clearFilters: "Clear",
    loadMore: "Load more",
    pageBound: (n: number) => `Showing up to ${n} rows at a time.`,
    noMore: "That is the end of the log for these filters.",

    /* ── F5 · reading one row ── */
    /**
     * 🔴 THE ACTOR COLUMN NOW HOLDS A PERSON, SO THESE ARE THE THREE THINGS IT
     * CAN SAY BESIDES A NAME. Each is a different fact rather than a degree of
     * the same one — see `auditActor`.
     */
    actorGone: "This account no longer exists",
    /** 🔴 admin-panel.md §8's specific confusion, named on the row it happens on. */
    actorVia: (operator: string) => `Acting as them · ${operator}`,
    /** ⚠️ Shown under the name, never instead of it. */
    actorIdLabel: "id",
    auditReason: "Why",
    /**
     * ⚠️ THE SUMMARY IS THE REASON, AND IT WAS WRITTEN AT THE MOMENT OF THE ACT.
     * Nothing on this screen composes one — a sentence assembled in the browser
     * from an action and a target would be a second account of what happened,
     * and the whole value of this table is that there is one.
     */
    auditNoReason: "No detail was recorded for this action.",

    /* ── F5 · paging ── */
    auditPage: (n: number) => `Page ${String(n)}`,
    auditPrev: "Previous",
    auditNext: "Next",
    /**
     * 🔴 THE END IS NAMED, AND SO IS THE FACT THAT THE COUNT IS NOT KNOWN. A
     * cursor-paged read cannot say "page 3 of 12" without a second counting
     * query over an append-only table that grows while it runs — so the screen
     * says which page it is on and whether there is another, which is what it
     * actually knows.
     */
    auditPageRows: (shown: number, size: number) =>
      `${String(shown)} rows, up to ${String(size)} a page`,
    auditEmpty: "Nothing matches those filters.",
    exportCsv: "Download as a spreadsheet",
    /** 🔴 The download is the copy that leaves the building. It says so. */
    exportWarning:
      "The spreadsheet contains the same rows, including children’s ids, and it leaves this machine with you. Downloading it is logged.",
    exportFailed: "That download did not start.",

    /**
     * 🔴 EVERY `AuditAction` IN PLAIN ENGLISH, AND THE ENUM VALUE IS STILL
     * SHOWN BESIDE IT.
     *
     * The column printed `SCHOOL_STATUS_CHANGE` — which is what the filter takes
     * and what a developer greps for, and is not a sentence. But replacing it
     * with prose would break the other half: an operator reading a screen and an
     * engineer reading a query have to be talking about the same value. So the
     * row carries both, the readable one first.
     *
     * ⚠️ A VALUE WITH NO ENTRY FALLS BACK TO THE ENUM NAME rather than to a
     * blank — `AuditAction` gains values (three MFA ones and `PLAY_AS_CHILD` in
     * recent slices), and a row labelled with nothing is an action that silently
     * disappeared from the table an incident is reconstructed from.
     */
    auditActionLabel: {
      ROSTER_IMPORT: "Imported a roster",
      SUPER_ADMIN_ACCESS: "Read something with unrestricted scope",
      SCHOOL_STATUS_CHANGE: "Moved a school through its lifecycle",
      ROLE_CHANGE: "Created or changed a staff account",
      STAFF_ROLE_CHANGE: "Changed what a staff member may do",
      CONTENT_PUBLISH: "Published or announced content",
      CONTENT_STATE_CHANGE: "Moved a release between states",
      FEATURE_FLAG_CHANGE: "Turned a feature on or off for a school",
      MAINTENANCE_MODE_CHANGE: "Turned maintenance mode on or off",
      DATA_EXPORT: "Exported a child’s data",
      DATA_ERASURE: "Erased a child’s data",
      CONSENT_GRANTED: "A parent gave consent",
      CONSENT_REVOKED: "A parent withdrew consent",
      ACADEMIC_ROLLOVER: "Rolled a school into a new year",
      RETENTION_POLICY_CHANGE: "Changed a school’s state",
      IMPERSONATION_START: "Started acting as somebody else",
      IMPERSONATION_END: "Stopped acting as somebody else",
      MFA_ENABLED: "Set up an authenticator",
      MFA_DISABLED: "Removed an authenticator",
      MFA_RECOVERY_USED: "Signed in with a recovery code",
      PLAY_AS_CHILD: "A teacher opened a game as a child",
      BILLING_CHANGE: "Changed a bill, quote, price or seat limit",
      SUPPORT_UPDATE: "Updated a support ticket",
      SCHOOL_UPDATE: "Changed a school's account details",
    } as Record<string, string>,

    /* ── X6 / X1 ── */
    systemTitle: "System",
    /**
     * ⚠️ THE PAGE HAD NO SUBTITLE AT ALL — four unrelated panels under one word.
     * This says what they have in common, which is the only reason they share a
     * screen: none of them belongs to a school.
     */
    systemSubtitle:
      "The machine rather than the customers: whether the product is working, the two switches, and the jobs.",
    systemHealthSection: "Is it working",
    systemSwitchesSection: "Switches",
    systemJobsSection: "Jobs",
    maintenanceTitle: "Maintenance mode",
    maintenanceOn: "Maintenance mode is ON",
    maintenanceOff: "Maintenance mode is off",
    maintenanceToggle: "Turn maintenance mode on",
    /** 🔴 The blast radius, in the two sentences that matter to an operator. */
    maintenanceMeaning:
      "Staff and platform screens are refused while this is on. Children and parents are never locked out — a classroom mid-session finishes its round.",
    maintenanceMessage: "What staff are told",
    maintenanceMessagePlaceholder:
      "One line. Staff being refused will read it.",
    maintenanceSince: (at: string) => `Changed ${at}`,
    maintenanceFailed: "That did not change.",
    /**
     * 🔴 THE MESSAGE SAVES ON A BUTTON, NOT ON BLUR. The box wrote on every
     * blur, so tabbing past it re-sent the current state as a fresh write — and
     * the button only appears once the text differs from what the server holds,
     * so there is nothing to press when there is nothing to save.
     */
    maintenanceSave: "Save the message",
    maintenanceSaving: "Saving…",

    flagsTitle: "Feature flags",
    flagsSubtitle:
      "Per school. Turning one off stays off through the next bulk enrolment.",
    flagsPickSchool: "School",
    flagsPickSchoolNone: "Choose a school",
    flagName: "Flag",
    flagState: "State",
    flagNote: "Why",
    flagOn: "On",
    flagOff: "Off",
    flagAdd: "Set a flag",
    flagAddName: "Flag name",
    flagAddNote: "Why (read months later by whoever wonders)",
    flagsEmpty: "This school has no flags set. It gets the defaults.",
    flagBetaHint: "content.beta is what puts a school on beta releases.",
    flagFailed: "That flag did not change.",
    set: "Set",

    /* ── searching a long list ── */
    searchSchools: "Find a school",
    searchSchoolsPlaceholder: "Name or id",
    /** 🔴 "Nothing matched" is a different fact from "there are no schools". */
    searchNoMatch: (query: string) => `No school matches “${query}”.`,
    /** 🔴 No silent caps — the number held back is named. */
    listHidden: (n: number) => `${n} more not shown`,
    listShowAll: "Show all",

    /* ── operators ── */
    operatorsTitle: "Who can operate this platform",
    /**
     * 🔴 THE REASON THIS SCREEN EXISTS, SAID ON IT. An operator who does not know
     * the seed cannot recreate them will not think a second account is urgent.
     */
    operatorsSubtitle:
      "A second operator is the only way back if this account is lost. Nothing else in the product can create one.",
    operatorsOnlyOne:
      "There is one operator account. If you lose it, only a database change can restore access.",
    operatorName: "Name",
    operatorEmail: "Email",
    operatorState: "State",
    operatorAdded: "Added",
    operatorActive: "Active",
    operatorInactive: "Deactivated",
    operatorAwaiting: "Waiting to be claimed",
    operatorAdd: "Add an operator",
    operatorAdding: "Adding…",
    operatorAddFailed: "That account was not created.",
    operatorEmailTaken: "An account with that email already exists.",
    /*
     * ⛔ THE THREE INVITE STRINGS STOOD HERE. An operator account is created
     * with a password the operator types and is ready to sign in — there is no
     * link to send. `operator.dto.ts` on the server carries the reasoning.
     */
    operatorReady: (name: string) =>
      `${name} can sign in now. Hand over the password you typed — Kalqy does not show it again.`,
    /**
     * 🔴 THE SENTENCE THAT REPLACES THE INVITATION'S SAFETY NET. A link expired
     * in 72 hours if it went astray; a password does not. What bounds this one
     * is the second factor, and an operator handing it over should know that
     * their colleague will be asked to set up an authenticator before they get
     * anywhere.
     */
    operatorMfaNote:
      "They will be asked to set up an authenticator app the first time they sign in. Nothing on this tier opens without it.",
    operatorDeactivate: "Deactivate",
    operatorDeactivating: "Deactivating…",
    operatorDeactivated: (sessions: number) =>
      `Deactivated. ${sessions} sessions ended.`,
    operatorDeactivateFailed: "That account did not change.",
    /** ⚠️ Both refusals stated, because a missing button otherwise reads as a bug. */
    operatorCannotSelf: "You cannot deactivate your own account.",
    operatorCannotLast:
      "This is the last active operator. Add another one first.",
    operatorConfirm: "Deactivate this operator?",
    operatorConfirmNote:
      "They are signed out everywhere. Their audit history is kept.",

    /* ── X5 · rollout targeting ── */
    rolloutTitle: "Who would a beta reach",
    /**
     * 🔴 THE SUBTITLE CARRIES THE SEPARATION. This screen plans; the flag is a
     * separate, audited act per school. An operator who expects a single "roll
     * out" button needs to know before they look for one.
     */
    rolloutSubtitle:
      "Work out which schools a beta release would reach, then turn it on school by school. Nothing here changes anything.",
    rolloutRegions: "States",
    /** ⚠️ "Every", not "Any" — an empty selection includes them all, and "any" reads as "none chosen yet". */
    rolloutAnyRegion: "Every state",
    rolloutMin: "Smallest school",
    rolloutMax: "Largest school",
    rolloutLimit: "Give it to at most",
    rolloutLimitHint: "Leave blank for every school that matches.",
    rolloutPlan: "Show me",
    rolloutPlanning: "Working it out…",
    rolloutFailed: "That plan did not run.",
    rolloutBandBackwards:
      "The smallest size cannot be larger than the largest.",
    rolloutConsidered: (n: number) => `Considered ${n} schools`,
    rolloutTargetTitle: "Would gain the beta",
    rolloutTargetNone: "No school matches that rule.",
    /** ⚠️ Smallest first is a safety property, not a sort preference — say so. */
    rolloutOrder:
      "Smallest school first, so a beta that breaks breaks in front of the fewest children.",
    rolloutAlreadyTitle: "Already on the beta",
    rolloutAlreadyNone: "No school is on the beta yet.",
    /** 🔴 Named, never silently truncated. */
    rolloutHeldTitle: "Held back by your limit",
    rolloutHeldNote: "These matched the rule. Raise the limit to include them.",
    rolloutOnlyActive:
      "Suspended and archived schools are never offered, and that is not a setting.",
    rolloutGrant: "Turn the beta on",
    rolloutGranting: "Turning on…",
    rolloutGranted: "On. It will pick up beta releases from now.",
    rolloutGrantFailed: "That school did not change.",
    rolloutGrantNote: "Rolled out from the content screen",
    schoolSize: "Children",

    /* ── X4 · support access ── */
    actAsTitle: "Act as someone at this school",
    /**
     * 🔴 THE BLAST RADIUS AND THE RECORD, BOTH BEFORE THE BUTTON. admin-panel.md
     * §8: never a silent view-as. An operator who understands only after the fact
     * that the log names them is an operator who has already acted.
     */
    actAsHint:
      "You will see exactly what they see, for fifteen minutes. Every action is recorded under your name and theirs.",
    actAsPickStaff: "Who",
    actAsPickStaffNone: "Choose a person",
    actAsNoStaff:
      "Nobody at this school can be acted as. Provision an admin first.",
    actAsReason: "Why",
    actAsReasonHint:
      "A sentence someone reading the log months from now will understand.",
    actAsReasonTooShort:
      "Say why, in a sentence — at least eight characters, on one line.",
    actAsStart: "Act as this person",
    actAsStarting: "Starting…",
    actAsFailed: "That session did not start.",
    /** ⚠️ The credential is live from here on — the operator's next click is as somebody else. */
    actAsStarted: (who: string) => `You are now acting as ${who}.`,
    impersonationEnd: "Stop acting as them",
    impersonationLeft: (clock: string) => `${clock} left`,
    /**
     * 🔴 THE TOKEN CANNOT BE REVOKED, SO "ENDED" MEANS "EXPIRED" — and the screen
     * says so rather than implying a button took the access away.
     */
    impersonationExpired: "That session has expired. You are yourself again.",
    impersonationEndNote:
      "Stopping records that you stopped. The credential itself dies on its own fifteen-minute clock.",

    /* ── F6 · the schools waiting for a phone call ── */
    /**
     * 🔴 "Requests", NOT "Leads", AND THE WORD IS A DECISION. A lead is a name
     * in a pipeline with a value attached to it; this product computes no such
     * figure and `platform-metrics.ts` refuses one in writing. What each row
     * actually is, is a school that asked to be contacted — so the screen calls
     * it that, and an operator reading the sidebar is told what is waiting for
     * them rather than what stage it is at.
     */
    requestsTitle: "Requests",
    requestsSubtitle:
      "Schools that filled in the form on the Connect page. Nothing here is an account yet — a school cannot create its own first admin, so somebody has to ring them.",
    /**
     * ⚠️ THE COUNT IS OF UNANSWERED ONES, OVER THE WHOLE TABLE. A total that
     * included the closed rows would grow for ever while telling an operator
     * nothing about their morning.
     */
    requestsWaiting: (n: number) => `${n} waiting for a first reply`,
    requestsEmpty: "Nobody has asked to be contacted yet.",
    requestsFilterAll: "All",
    requestsWhen: "Received",
    requestsWho: "Who",
    requestsSchool: "School",
    requestsWhere: "Where",
    requestsSize: "Children",
    requestsInterest: "Wants to improve",
    requestsHeard: "Heard about us",
    requestsStatus: "Status",
    /** The four the row can be in. Read as a sentence about the school. */
    requestsStatusNEW: "Not answered",
    requestsStatusCONTACTED: "Rung",
    requestsStatusIN_PROGRESS: "In progress",
    requestsStatusCLOSED: "Closed",
    /**
     * ⚠️ SAID ON SCREEN, BECAUSE THE ROW LOOKS EDITABLE AND IS NOT. A status
     * dropdown beside ten fixed fields invites the question, and an operator who
     * expects to correct a typo here and cannot will assume the screen is broken.
     */
    requestsReadOnly:
      "The details are as the school typed them and cannot be edited here. Only the status is yours.",
    requestsContactedAt: (when: string) => `First picked up ${when}`,
    requestsStatusFailed: "That status did not save. Try it again.",
    requestsNoInterest: "Not said",

    /* ── the machinery ── */
    jobsTitle: "Background jobs",
    jobsSubtitle:
      "Run one now. This works even when the schedule is switched off.",
    jobRun: "Run now",
    jobRunning: "Running…",
    /**
     * 🔴 WHAT EACH JOB IS, IN ONE CLAUSE. The panel listed `notification-outbox`,
     * `weekly-digest` and `data-retention` — the strings
     * `jobs.controller.ts` matches a path parameter against, which is exactly
     * right in a URL and is not a thing to put in front of somebody deciding
     * whether to press a button next to it. One of the three deletes children's
     * data.
     *
     * ⚠️ THE SLUG STAYS, SMALL, BESIDE THE NAME. It is what the route takes and
     * what a log line says; dropping it would leave the screen and the server
     * with no shared vocabulary.
     */
    jobLabel: {
      "notification-outbox": "Send queued messages",
      "weekly-digest": "Build this week's parent digests",
      "data-retention": "Delete data past its retention date",
    } as Record<string, string>,
    jobWhat: {
      "notification-outbox": "Anything waiting to go out to a family.",
      "weekly-digest": "The summary a parent gets once a week.",
      "data-retention": "The only job here that destroys anything.",
    } as Record<string, string>,
    /**
     * 🔴 THE ONLY JOB A CLICK CAN DESTROY DATA WITH. `RETENTION_DRY_RUN` may make
     * it harmless, but that flag is in the environment and not on this screen —
     * so the operator is asked, and told which mode it actually ran in after.
     */
    jobConfirmRetention:
      "This job deletes expired data. Whether it really deletes depends on a setting you cannot see from here. Run it?",
    jobResult: (processed: number, failed: number, ms: number) =>
      `${processed} processed, ${failed} failed, in ${ms} ms`,
    jobDryRun: "Dry run — nothing was deleted",
    jobLive: "Live run — data was deleted",
    jobFailed: "That job did not run.",
    jobConfirm: "Yes, run it",
    /* `cancel` is already in this dictionary — F2's archive dialog owns it. */

    healthTitle: "Is the product working",
    healthSubtitle:
      "Tracking failures, the slowest tenth of frame rates, and how many children finished by tapping instead.",
    healthWindow: (days: number) => `Last ${days} days`,
    healthTracking: "Rounds abandoned to tracking",
    healthTrackingPair: (abandoned: number, started: number) =>
      `${abandoned} of ${started} rounds started`,
    healthFps: "Frame rate",
    healthFpsPair: (p10: number, median: number, samples: number) =>
      `slowest tenth ${p10}, middle ${median}, from ${samples} readings`,
    healthFpsThin: "Too few readings to give a percentile",
    healthBelowFloor:
      "Below the floor — the degradation ladder should be engaging. Check that it is.",
    healthFallback: "Finished by tapping instead",
    healthFallbackPair: (used: number, completed: number) =>
      `${used} of ${completed} completed rounds`,
    healthFallbackWhy:
      "A rise here means the camera path is failing and children are routing around it — which looks healthy in every other number.",
    /** 🔴 A verdict, never a bare rate. Below the floor the rate is withheld rather than printed. */
    verdictHealthy: "Working",
    verdictInvestigate: "Worth investigating",
    verdictNotWorking: "Not working",
    verdictTooFewRounds: "Too few rounds to say",
    healthWorseThan: "Worse than the overall figure",
    healthNoSegments: "No segment is worse than the overall figure.",
    healthByBand: "By age band",
    healthByGame: "By game",
    healthByDevice: "By device",

    /*
     * ── which games the platform is playing ─────────────────────────────────
     *
     * 🔴 A PRODUCT QUESTION, AND THE HINT SAYS WHOSE. Everything else on F1 is
     * about a school; this is about the five games, and no per-school figure adds
     * up to it.
     *
     * 🔴 THE WINDOW IS A PARAMETER, NEVER A NUMBER TYPED HERE. The server owns
     * it and sends it back, so the sentence cannot describe a period the query
     * did not use — the same rule OD-39/OD-72 apply to denominators, applied to
     * time.
     */
    platformGamesTitle: "What is being played",
    platformGamesHint: (days: number) =>
      `Across every school, over the last ${String(days)} days. A game with nothing against it has not been opened anywhere in that window.`,

    /* ── every child on the platform ── */
    childrenTitle: "Children",
    childrenSubtitle:
      "Every child enrolled on Kalqy, and what they have actually played. Opening one is recorded against your account.",
    /**
     * 🔴 SAID BEFORE THE OPERATOR CLICKS, NOT AFTER. Reading this list writes an
     * audit row, and opening a single child writes one naming them — the same
     * courtesy F5 pays about the audit log itself, and for the same reason: an
     * operator who learns it from the data mid-incident learns it too late.
     */
    childrenIsAudited:
      "Opening this list is recorded. Opening one child is recorded with their name.",
    childSearch: "Search",
    childSearchHint: "Name, roll number, class, school or parent.",
    childSchool: "School",
    childEverySchool: "Every school",
    childActivity: "Activity",
    /* One label per state. "Quiet" and "never" are different children and
       different conversations — `childrenModel.ts` keeps them apart. */
    childActivityAll: "Everyone",
    childActivityRecent: "Played this week",
    childActivityQuiet: "Quiet for a week",
    childActivityNever: "Never played",
    /**
     * 🔴 THE STATE ONLY THIS TIER CAN SEE. `recorded: false` means the events
     * were never written at all (ADR-030), so to every count in the product
     * these children look like children who have done nothing. Saying "never
     * played" about them would send somebody to chase a child who has been
     * playing all term.
     */
    childActivityUnrecorded: "Playing, nothing recorded",
    childUnattributed: "In no class",
    childUnattributedHint:
      "No class means no school, so no school admin can see them. Every one is a roster that went wrong.",
    childRoll: "Roll",
    childClass: "Class",
    childSessions: "Sittings",
    childRounds: "Rounds",
    childGames: "Games",
    childLastPlayed: "Last played",
    childNever: "Never",
    childOpen: "Open",
    childCount: (shown: number, total: number) =>
      `Showing ${String(shown)} of ${String(total)} children`,
    childTruncated: (limit: number) =>
      `This page stops at ${String(limit)} children. Narrow the search to see the rest.`,
    childNoneMatch: "No child matches that.",
    childClearFilters: "Clear",
    childExport: "Download CSV",
    childExportWarning:
      "The file names real children and leaves this machine with you. It carries no parent names and no phone numbers.",
    /* ── one child ── */
    childBack: "Back to every child",
    childPlayedPair: (correct: number, total: number) =>
      `${String(correct)} of ${String(total)} answers right`,
    childTooFewAnswers: (n: number) =>
      `Fewer than ${String(n)} answers — too few to read anything into.`,
    childMinutes: (minutes: number, measured: number, of: number) =>
      `${String(minutes)} minutes across ${String(measured)} of ${String(of)} sittings`,
    /** ⚠️ A sitting nothing closed has no duration. Never rendered as zero. */
    childMinutesUnknown:
      "No sitting on this page has an end time, so there are no minutes to total.",
    childSessionsTitle: "Every sitting",
    childSessionsHint: (shown: number, total: number) =>
      `The most recent ${String(shown)} of ${String(total)}, newest first.`,
    childSessionStarted: "Started",
    childSessionLength: "Length",
    childSessionEvents: "Events",
    childSessionOpen: "Still open",
    childSessionUnrecorded: "Not recorded",
    childSessionUnrecordedHint:
      "Recording was off for this sitting, so nothing was written. It is not an empty session.",
    childSessionVersion: "Build",
    childNothingPlayed:
      "This child has never played. There is nothing to show yet.",
    childGamesTitle: "What this child has played",

    /* ── F4b · release notices ── */
    noticesTitle: "Tell people what changed",
    noticesSubtitle:
      "A short message at the top of a dashboard, for the roles a release actually affected. Nothing is sent — they see it next time they sign in.",
    noticeHeadline: "One line",
    noticeBody: "What changed",
    noticeRoles: "Who sees it",
    /** 🔴 The refusal the server makes, said on the form so it is never a 400. */
    noticeRolesRequired: "Choose at least one role.",
    noticePost: "Post it",
    noticePosting: "Posting…",
    noticePosted: "Posted. People with those roles will see it when they next sign in.",
    noticeFailed: "That notice was not posted.",
    noticeWithdraw: "Stop showing",
    noticeWithdrawn: "Withdrawn",
    noticeShowing: "Showing",
    noticeNone: "Nothing has been announced yet.",
    noticeReach:
      "Somebody who never signs in never sees this. It is a notice board, not a message.",
    noticeFor: (version: string) => `About ${version}`,

    /* ── shared ── */
    dash: "—",
    loadFailed: "Couldn’t load this.",
    /** 🔴 A 401/403 here means "not a platform operator", not "offline". */
    notAnOperator:
      "This tier is for platform operators. Sign in with an operator account.",
    lastWorked: (at: string) => `Last loaded ${at}`,
    neverWorked: "It hasn’t loaded yet",
    retry: "Try again",
    /** The shell's doorway to G1–G4. Every adult tier renders this chrome. */
    settings: "Settings",
    signOut: "Sign out",
    /** The phone thumb bar's last slot, and the sheet it opens. */
    more: "More",
    close: "Close",
    /** The parent's sub-view of Home (ADR-051), listed in that sheet. */
    privacy: "Privacy & consent",
    /**
     * The parent tier's way to a person, at the foot of its sidebar.
     *
     * 🔴 IT NAMES THE SCHOOL AND NOT US, AND THAT IS THE WHOLE COPY DECISION.
     * The control opens `MessageThread` — a parent and their child's class
     * teacher, with Kalqy not in it. A block reading "Need help?" over that
     * channel would send billing and login questions to somebody who cannot
     * answer them and would never see a reply.
     */
    helpTitle: "Questions about your child?",
    helpHint: "Message their teacher",
    /*
     * ⚠️ THE BADGE'S SPOKEN FORM. The disc beside "Messages" is `aria-hidden` —
     * §12 forbids colour as the only carrier, and a magenta circle is exactly
     * that for a screen-reader user. This is the sentence they get instead.
     */
    unreadMessages: (n: number) =>
      n === 1 ? "1 unread message" : `${String(n)} unread messages`,
    /**
     * F4b's one control. ⚠️ A WORD, NOT AN "×" — the strip sits above every
     * screen a signed-in adult opens, and an unlabelled glyph top-right of a
     * page is the shape people click without reading.
     */
    noticeDismiss: "Got it",
    back: "Back",
    /** X4 — admin-panel.md §8: never a silent view-as. */
    impersonating:
      "You are signed in as somebody else. Everything you do is recorded under both names.",
  },
} as const;

/**
 * 🔴 A 401 OR 403 ON A PLATFORM SCREEN MEANS "NOT AN OPERATOR", NOT "OFFLINE".
 *
 * `adminCopy.ts` makes the identical argument one tier down, and it is sharper
 * here: every endpoint behind this tier is `@Roles('SUPER_ADMIN')` **and**
 * re-checks in the service, so the overwhelmingly likely cause of a refusal is
 * that a school admin opened an operator URL. Telling them the network failed
 * sends them hunting a fault that does not exist.
 */
export function describePlatformError(err: Error | null, lang: Lang): string {
  const t = PLATFORM_COPY[lang];
  const status = (err as { status?: number } | null)?.status;
  if (status === 401 || status === 403) return t.notAnOperator;
  return t.loadFailed;
}

/**
 * The product-health verdict, as a word.
 *
 * 🔴 `TOO_FEW_ROUNDS` IS A VERDICT AND NOT A MISSING VALUE. `product-health.ts`
 * returns `rate: null` below the sample floor precisely so a dashboard cannot
 * print a rate nobody should read — and rendering that as a dash would leave an
 * operator to guess whether the camera is fine or the query is broken. The
 * fourth word says which.
 */
export function trackingVerdictText(
  verdict: "HEALTHY" | "INVESTIGATE" | "NOT_WORKING" | "TOO_FEW_ROUNDS",
  lang: Lang,
): string {
  const t = PLATFORM_COPY[lang];
  switch (verdict) {
    case "HEALTHY":
      return t.verdictHealthy;
    case "INVESTIGATE":
      return t.verdictInvestigate;
    case "NOT_WORKING":
      return t.verdictNotWorking;
    default:
      return t.verdictTooFewRounds;
  }
}

export function healthVerdictText(
  verdict: "OK" | "WATCH" | "AT_RISK",
  lang: Lang,
): string {
  const t = PLATFORM_COPY[lang];
  switch (verdict) {
    case "AT_RISK":
      return t.verdictAtRisk;
    case "WATCH":
      return t.verdictWatch;
    default:
      return t.verdictOk;
  }
}

/**
 * Why a password was refused, in one sentence.
 *
 * 🔴 ONE SENTENCE PER REASON, NEVER A SINGLE *"that password is not allowed"*.
 * A refusal with no cause is how somebody ends up appending digits until
 * something is accepted — which produces exactly the class of password the
 * policy exists to refuse.
 */
export function passwordProblemText(
  problem: PasswordProblem,
  lang: Lang,
): string {
  const t = PLATFORM_COPY[lang];
  switch (problem) {
    case "TOO_SHORT":
      return t.passwordTooShort(OPERATOR_PASSWORD_MIN);
    case "TOO_LONG":
      return t.passwordTooLong(OPERATOR_PASSWORD_MAX);
    case "EDGE_WHITESPACE":
      return t.passwordEdgeWhitespace;
    case "ONE_CLASS":
      return t.passwordOneClass;
    case "PREDICTABLE":
      return t.passwordPredictable;
    case "ECHOES_IDENTITY":
      return t.passwordEchoesIdentity;
    default:
      return t.passwordMismatch;
  }
}

export function signalText(code: SignalCode, lang: Lang): string {
  const t = PLATFORM_COPY[lang];
  switch (code) {
    case "NO_SEATS":
      return t.signalNoSeats;
    case "NO_TEACHERS":
      return t.signalNoTeachers;
    case "NOBODY_PLAYED":
      return t.signalNobodyPlayed;
    default:
      return t.signalLowEngagement;
  }
}

/**
 * Why a school is, or is not, flagged.
 *
 * 🔴 A REASON RATHER THAN A BLANK. *"Why is this school not on the list"* is the
 * question F1 gets asked, and *"it has six children"* is a different answer from
 * *"it is fine"* — `platform-metrics.ts` built the enum for exactly that.
 */
export function riskText(risk: RiskVerdict, lang: Lang): string {
  const t = PLATFORM_COPY[lang];
  switch (risk) {
    case "AT_RISK":
      return t.riskAtRisk;
    case "TOO_SMALL_TO_JUDGE":
      return t.riskTooSmall;
    case "NOT_ENOUGH_HISTORY":
      return t.riskNotEnoughHistory;
    case "NOT_ACTIVE":
      return t.riskNotActive;
    default:
      return t.riskOk;
  }
}

export const STATUS_LABEL: Record<SchoolStatus, Record<Lang, string>> = {
  CREATED: { en: PLATFORM_COPY.en.statusCreated },
  ONBOARDING: { en: PLATFORM_COPY.en.statusOnboarding },
  ACTIVE: { en: PLATFORM_COPY.en.statusActive },
  SUSPENDED: { en: PLATFORM_COPY.en.statusSuspended },
  ARCHIVED: { en: PLATFORM_COPY.en.statusArchived },
};

export const CONTENT_STATE_LABEL: Record<ContentState, Record<Lang, string>> = {
  DRAFT: { en: PLATFORM_COPY.en.stateDraft },
  BETA: { en: PLATFORM_COPY.en.stateBeta },
  PUBLISHED: { en: PLATFORM_COPY.en.statePublished },
  RETIRED: { en: PLATFORM_COPY.en.stateRetired },
};

/**
 * The Indian state or union territory a school is in.
 *
 * 🔴 IT USED TO BE `IN` / `US` / `EU`, AND THE CODE WAS ALSO THE LABEL. That
 * was defensible while the value was a jurisdiction — a translated `EU` would
 * have been a second vocabulary for the field a child's deletion date hangs
 * off. It stopped being defensible the moment the value became a state: `WB`
 * is West Bengal to a database and to nobody else, and an operator onboarding a
 * school in Kolkata should not have to know ISO 3166-2 to find it in a list.
 *
 * ⚠️ SO THE CODE IS STILL WHAT IS STORED AND SENT, AND THE NAME IS ONLY WHAT IS
 * READ. Every `<option>` carries the code as its value; this table decides what
 * the option says. Nothing derives a code from a name.
 *
 * ⚠️ AND IT IS NOT TRANSLATED, which is `BAND_LABEL`'s argument and survives
 * the change: these are the official English names in the Registrar General's
 * own lists, and the product is English only (ADR-044).
 */
export const REGION_LABEL: Record<Region, string> = {
  AN: "Andaman & Nicobar Islands",
  AP: "Andhra Pradesh",
  AR: "Arunachal Pradesh",
  AS: "Assam",
  BR: "Bihar",
  CH: "Chandigarh",
  CT: "Chhattisgarh",
  DH: "Dadra & Nagar Haveli and Daman & Diu",
  DL: "Delhi",
  GA: "Goa",
  GJ: "Gujarat",
  HR: "Haryana",
  HP: "Himachal Pradesh",
  JK: "Jammu & Kashmir",
  JH: "Jharkhand",
  KA: "Karnataka",
  KL: "Kerala",
  LA: "Ladakh",
  LD: "Lakshadweep",
  MP: "Madhya Pradesh",
  MH: "Maharashtra",
  MN: "Manipur",
  ML: "Meghalaya",
  MZ: "Mizoram",
  NL: "Nagaland",
  OR: "Odisha",
  PY: "Puducherry",
  PB: "Punjab",
  RJ: "Rajasthan",
  SK: "Sikkim",
  TN: "Tamil Nadu",
  TG: "Telangana",
  TR: "Tripura",
  UP: "Uttar Pradesh",
  UT: "Uttarakhand",
  WB: "West Bengal",
};

/**
 * The states, in the order a dropdown should offer them.
 *
 * 🔴 ALPHABETICAL BY NAME, NOT BY CODE. The codes sort `AN, AP, AR, AS, BR…`,
 * which is nearly alphabetical by name and then is not — `CT` (Chhattisgarh)
 * lands after `CH` (Chandigarh) and `DH` before `DL`, so a list sorted by code
 * puts Delhi in a place nobody scanning for the letter D would look. The value
 * an operator is searching for is the name.
 *
 * ⚠️ COMPUTED FROM `REGION_LABEL` RATHER THAN TYPED OUT AGAIN. A second list of
 * thirty-six states is a second list to forget to edit, and the failure is a
 * state that exists in the database and cannot be picked in the browser.
 */
export const REGIONS_BY_NAME: readonly Region[] = (
  Object.keys(REGION_LABEL) as Region[]
).sort((a, b) => REGION_LABEL[a].localeCompare(REGION_LABEL[b]));
